import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Search, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LEAD_STATUSES = ["new", "contacted", "estimate_scheduled", "estimate_sent", "assigned", "in_progress", "completed", "cancelled", "no_response"];
const label = (s) => (s || "new").replace(/_/g, " ");
const inputCls = "h-10 border border-border px-3 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-flame";

const STATUS_COLORS = {
  new: "bg-orange-50 text-flame border-flame",
  contacted: "bg-blue-50 text-blue-600 border-blue-300",
  estimate_scheduled: "bg-amber-50 text-amber-600 border-amber-300",
  estimate_sent: "bg-amber-50 text-amber-700 border-amber-400",
  assigned: "bg-purple-50 text-purple-600 border-purple-300",
  in_progress: "bg-blue-50 text-blue-700 border-blue-400",
  completed: "bg-green-50 text-green-700 border-green-400",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
  no_response: "bg-slate-100 text-slate-400 border-slate-300",
};

const LeadDetail = ({ id, providers, onChanged }) => {
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/leads/${id}`).then((r) => { setLead(r.data); onChanged(); }).catch(() => {});
  }, [id, onChanged]);
  useEffect(() => { load(); }, [load]);

  const update = async (payload) => {
    try {
      const { data } = await api.put(`/admin/leads/${id}`, payload);
      setLead(data);
      onChanged();
      toast.success("Lead updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const addNote = async () => {
    try {
      await api.post(`/admin/leads/${id}/notes`, { text: note });
      setNote("");
      load();
      toast.success("Note saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!lead) return <p className="text-xs text-slate-400 p-4">Loading lead…</p>;

  return (
    <div className="bg-[#F4F5F7] border-t border-border p-5 space-y-5" data-testid={`lead-detail-${id}`}>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs">
        {[
          ["Full name", lead.contact_name], ["Phone", lead.contact_phone],
          ["Email", lead.contact_email], ["Address", lead.address || "—"],
          ["City / State / ZIP", `${lead.city}, ${lead.state} ${lead.zip_code}`],
          ["Service", lead.category_name],
          ["Urgency", lead.is_emergency ? "URGENT / EMERGENCY" : "Non-urgent"],
          ["Budget", lead.budget_range], ["Estimate method", lead.estimate_method],
          ["Requested date", lead.preferred_date || "Flexible"],
          ["Requested time", lead.preferred_time_window || "Flexible"],
          ["Contact preference", lead.contact_preference],
          ["Submitted", new Date(lead.created_at).toLocaleString()],
          ["Lead source", lead.lead_source || "Prime Pro Connect Services"],
        ].map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="text-slate-400 w-32 shrink-0">{k}</dt>
            <dd className={k === "Urgency" && lead.is_emergency ? "text-red-600 font-bold" : "text-navy"}>{v || "—"}</dd>
          </div>
        ))}
      </dl>
      <div className="text-xs text-slate-500">
        <strong className="text-navy">Job description:</strong> {lead.description}
      </div>

      {lead.photo_ids?.length > 0 && (
        <div data-testid={`lead-photos-${id}`}>
          <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Photos ({lead.photo_ids.length})</p>
          <div className="flex flex-wrap gap-2">
            {lead.photo_ids.map((pid) => (
              <img key={pid} src={`${BACKEND}/api/files/${pid}`} alt="Project" className="w-24 h-24 object-cover border border-border" />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-1">Assigned provider</p>
          <select data-testid={`lead-provider-select-${id}`} value={lead.assigned_provider_id || ""}
            onChange={(e) => update({ assigned_provider_id: e.target.value })}
            className={inputCls}>
            <option value="">Unassigned</option>
            {providers.map((p) => <option key={p.id} value={p.id}>{p.company_name}</option>)}
          </select>
        </div>
        {lead.lead_status !== "completed" && (
          <button data-testid={`lead-complete-btn-${id}`} onClick={() => update({ lead_status: "completed" })}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2.5 mt-4 transition-colors">
            <CheckCircle2 size={13} /> Mark Complete
          </button>
        )}
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Internal notes ({(lead.notes || []).length})</p>
        {(lead.notes || []).map((n) => (
          <p key={n.id} className="text-xs text-slate-600 border-t border-border py-1.5">
            {n.text} <span className="text-slate-400">— {n.author_name}, {new Date(n.created_at).toLocaleString()}</span>
          </p>
        ))}
        <div className="flex gap-2 mt-2">
          <input data-testid={`lead-note-input-${id}`} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add internal note…" className={`${inputCls} flex-1`} />
          <button data-testid={`lead-note-save-${id}`} disabled={!note.trim()} onClick={addNote}
            className="bg-navy hover:bg-navy-light disabled:opacity-40 text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 transition-colors">
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
};

export const PrimeProLeads = ({ onStatsChange }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [f, setF] = useState({ q: "", status: "", service: "", city: "", zip_code: "", urgent: "", provider: "", unread: "", date_from: "", date_to: "" });

  const loadStats = useCallback(() => {
    api.get("/admin/leads/stats").then((r) => { setStats(r.data); onStatsChange?.(r.data); }).catch(() => {});
  }, [onStatsChange]);

  const loadLeads = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [f]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  useEffect(() => {
    api.get("/admin/professionals").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const refreshAll = useCallback(() => { loadLeads(); loadStats(); }, [loadLeads, loadStats]);

  const setStatus = async (id, lead_status) => {
    try {
      await api.put(`/admin/leads/${id}`, { lead_status });
      refreshAll();
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="primepro-leads-section">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="leads-stats">
          {[
            ["Total leads", stats.total, "total"], ["New", stats.new, "new"],
            ["Urgent", stats.urgent, "urgent"], ["Today", stats.today, "today"],
            ["This week", stats.week, "week"], ["Assigned", stats.assigned, "assigned"],
            ["Completed", stats.completed, "completed"],
          ].map(([lbl, val, key]) => (
            <div key={lbl} data-testid={`stat-${key}`} className={`bg-white border p-4 ${key === "urgent" && val > 0 ? "border-red-400" : "border-border"}`}>
              <p className={`font-heading font-black text-2xl ${key === "urgent" && val > 0 ? "text-red-600" : "text-navy"}`}>{val}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{lbl}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-border p-4 mb-5 flex flex-wrap gap-2 items-center" data-testid="leads-filters">
        <div className="flex items-center border border-border px-2 h-10 flex-1 min-w-[180px]">
          <Search size={14} className="text-slate-400" />
          <input data-testid="leads-filter-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            placeholder="Search name, phone, email, description…" className="flex-1 px-2 text-xs focus:outline-none" />
        </div>
        <select data-testid="leads-filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={`${inputCls} capitalize`}>
          <option value="">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select data-testid="leads-filter-service" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} className={inputCls}>
          <option value="">All services</option>
          {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
        <input data-testid="leads-filter-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" className={`${inputCls} w-24`} />
        <input data-testid="leads-filter-zip" value={f.zip_code} onChange={(e) => setF({ ...f, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="ZIP" className={`${inputCls} w-20`} />
        <select data-testid="leads-filter-urgent" value={f.urgent} onChange={(e) => setF({ ...f, urgent: e.target.value })} className={inputCls}>
          <option value="">Urgent + non-urgent</option>
          <option value="true">Urgent only</option>
          <option value="false">Non-urgent only</option>
        </select>
        <select data-testid="leads-filter-provider" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} className={inputCls}>
          <option value="">Any provider</option>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.company_name}</option>)}
        </select>
        <input data-testid="leads-filter-datefrom" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inputCls} />
        <input data-testid="leads-filter-dateto" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inputCls} />
        <label className="flex items-center gap-1.5 text-xs text-navy font-medium">
          <input data-testid="leads-filter-unread" type="checkbox" checked={f.unread === "true"}
            onChange={(e) => setF({ ...f, unread: e.target.checked ? "true" : "" })} className="accent-[#FF5A00]" />
          Unread only
        </label>
      </div>

      {leads === null ? (
        <p className="text-slate-400 text-sm">Loading leads…</p>
      ) : leads.length === 0 ? (
        <p className="text-slate-400 text-sm bg-white border border-border p-8 text-center" data-testid="leads-empty">No leads match these filters.</p>
      ) : (
        <div className="space-y-2" data-testid="leads-list">
          {leads.map((l) => (
            <div key={l.id} className={`bg-white border ${l.is_emergency ? "border-red-400 border-l-4" : "border-border"}`}>
              <div className="p-4 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3">
                  {l.unread && <span data-testid={`lead-unread-dot-${l.id}`} className="w-2.5 h-2.5 bg-flame rounded-full mt-1.5 shrink-0" title="Unread" />}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-black text-navy tracking-tight">{l.contact_name}</h3>
                      <span className="text-xs text-slate-400">· {l.category_name}</span>
                      {l.is_emergency && (
                        <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
                          <AlertTriangle size={10} /> Urgent
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {l.city}, {l.zip_code} · {l.contact_phone} · {new Date(l.created_at).toLocaleString()}
                      {l.assigned_provider_name && <span className="text-purple-600 font-medium"> · Assigned: {l.assigned_provider_name}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold uppercase tracking-widest border px-2 py-1 ${STATUS_COLORS[l.lead_status] || STATUS_COLORS.new}`}>
                    {label(l.lead_status)}
                  </span>
                  <select data-testid={`lead-status-select-${l.id}`} value={l.lead_status || "new"}
                    onChange={(e) => setStatus(l.id, e.target.value)} className={`${inputCls} capitalize`}>
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                  </select>
                  <button data-testid={`lead-expand-${l.id}`} onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                    className="text-slate-400 hover:text-navy">
                    {expanded === l.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>
              {expanded === l.id && <LeadDetail id={l.id} providers={providers} onChanged={refreshAll} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrimeProLeads;
